export interface TokenRange {
  start: number;
  end: number;
}

export interface SentenceTokenSpan {
  sourceIndex: number;
  start: number;
  end: number;
}

export interface DialogueLine {
  start: number;
  end: number;
  text: string;
}

const sentenceEnd = /[。！？!?‼⁉]/u;
const sentenceEndContinuation = /[。！？!?‼⁉」』）】〉》〕〗〙〛]/u;
const phraseBreak = /[、，,]/u;
const phraseBreakContinuation = /[」』）】〉》〕〗〙〛]/u;
const readableCharacter = /[\p{L}\p{N}]/u;
const minimumCommaChunkCharacters = 5;
const minimumCommaSplitCharacters = 14;

/**
 * Turns an OCR run into the dialogue-sized lines a reader expects to see.
 * Strong punctuation and explicit newlines always end a line. A Japanese comma
 * may also divide a long OCR run when both sides remain useful reading chunks;
 * short remarks and list-like fragments stay together.
 */
export function splitDialogueLines(sentence: string): DialogueLine[] {
  const lines: DialogueLine[] = [];
  let start = 0;

  const trimmedBounds = (rawStart: number, rawEnd: number): TokenRange => {
    let lineStart = rawStart;
    let lineEnd = rawEnd;
    while (lineStart < lineEnd && /\s/u.test(sentence[lineStart])) lineStart += 1;
    while (lineEnd > lineStart && /\s/u.test(sentence[lineEnd - 1])) lineEnd -= 1;
    return { start: lineStart, end: lineEnd };
  };
  const readableLength = (rangeStart: number, rangeEnd: number) => {
    let length = 0;
    for (let index = rangeStart; index < rangeEnd; index += 1) {
      if (readableCharacter.test(sentence[index])) length += 1;
    }
    return length;
  };
  const push = (rawStart: number, rawEnd: number) => {
    const bounds = trimmedBounds(rawStart, rawEnd);
    if (bounds.end <= bounds.start) return;
    lines.push({ ...bounds, text: sentence.slice(bounds.start, bounds.end) });
  };
  const append = (rawStart: number, rawEnd: number) => {
    const bounds = trimmedBounds(rawStart, rawEnd);
    if (
      bounds.end <= bounds.start ||
      readableLength(bounds.start, bounds.end) < minimumCommaSplitCharacters
    ) {
      push(bounds.start, bounds.end);
      return;
    }

    let chunkStart = bounds.start;
    for (let index = bounds.start; index < bounds.end; index += 1) {
      if (!phraseBreak.test(sentence[index])) continue;
      let chunkEnd = index + 1;
      while (chunkEnd < bounds.end && phraseBreakContinuation.test(sentence[chunkEnd])) chunkEnd += 1;
      if (
        readableLength(chunkStart, chunkEnd) < minimumCommaChunkCharacters ||
        readableLength(chunkEnd, bounds.end) < minimumCommaChunkCharacters
      ) continue;
      push(chunkStart, chunkEnd);
      chunkStart = chunkEnd;
      index = chunkEnd - 1;
    }
    push(chunkStart, bounds.end);
  };

  for (let index = 0; index < sentence.length;) {
    if (sentence[index] === "\n" || sentence[index] === "\r") {
      append(start, index);
      while (index < sentence.length && (sentence[index] === "\n" || sentence[index] === "\r")) index += 1;
      start = index;
      continue;
    }
    if (!sentenceEnd.test(sentence[index])) {
      index += 1;
      continue;
    }
    let end = index + 1;
    while (end < sentence.length && sentenceEndContinuation.test(sentence[end])) end += 1;
    append(start, end);
    start = end;
    index = end;
  }
  append(start, sentence.length);
  return lines.length > 0 ? lines : sentence ? [{ start: 0, end: sentence.length, text: sentence }] : [];
}

/**
 * Maps Sudachi token surfaces back onto the sentence shown in the reader.
 *
 * Tokenisation includes punctuation that is deliberately omitted from the word
 * picker, so retaining the original source index lets the reader highlight the
 * exact characters for either an individual word or a selected compound.
 */
export function tokenSpansInSentence(sentence: string, tokenSurfaces: readonly string[]): SentenceTokenSpan[] {
  const spans: SentenceTokenSpan[] = [];
  let searchFrom = 0;

  tokenSurfaces.forEach((surface, sourceIndex) => {
    if (!surface) return;
    const start = sentence.indexOf(surface, searchFrom);
    if (start < 0) return;
    spans.push({ sourceIndex, start, end: start + surface.length });
    searchFrom = start + surface.length;
  });

  return spans;
}

export function nextTokenRange(current: TokenRange | null, index: number, combinableIndices: Set<number>): TokenRange | null {
  if (!current) return { start: index, end: index };
  if (index >= current.start && index <= current.end) {
    if (current.start === current.end) return null;
    if (index === current.start) return { start: current.start + 1, end: current.end };
    if (index === current.end) return { start: current.start, end: current.end - 1 };
    return { start: index, end: index };
  }
  if (combinableIndices.has(index) && index === current.start - 1) return { start: index, end: current.end };
  if (combinableIndices.has(index) && index === current.end + 1) return { start: current.start, end: index };
  return { start: index, end: index };
}
